import React, { useState, useEffect } from "react"
import { TextField, useTheme } from "@material-ui/core"
import { MentionsInput, Mention } from "@impelsys/react-mentions"
import { useQueryUsers, useQueryGroups, useQueryKnowledges, useQueryMentionableJobs } from "../../data"
import { UserMentionListItem } from ".."
import { MentionListItem } from "./MentionListItem"
import { useAuth } from "../../services"
import { MentionProcessListItem } from "./MentionProcessListItem"
import { mapToIds } from "../../utils"
import markupToRegex from "./utils/markupToRegex"

const getStyles = (theme, { height, minHeight, readOnly, multiline, showingSubMenu, margin }) => {
  const multilinePadding = margin === "dense" ? "10.5px 14px" : "18.5px 14px"

  return {
    control: {},
    input: multiline
      ? {
          height: readOnly ? "100%" : height || "auto",
          minHeight,
          resize: "none",
          overflow: "auto",
          width: "100%",
          border: "none",
          outline: "none",
          padding: multilinePadding,
          margin: 0,
          wordBreak: "break-word"
        }
      : {
          boxSizing: "border-box",
          height: "100%",
          width: "100%",
          lineHeight: "20px",
          border: "none",
          outline: "none",
          margin: 0
        },
    suggestions: {
      list: {
        zIndex: 999,
        position: "relative",
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.grey[200]}`,
        padding: showingSubMenu ? 0 : 8,
        fontSize: 13,
        marginTop: 10,
        borderRadius: theme.spacing(0.5),
        maxHeight: 200,
        overflow: "auto",
        minWidth: 320
      },
      item: {
        padding: showingSubMenu ? 0 : "5px 10px",
        borderRadius: theme.spacing(0.5),
        fontSize: 13,
        "&focused": {
          backgroundColor: theme.palette.primary.backgroundDark
        }
      }
    },
    highlighter: multiline
      ? {
          overflow: "hidden",
          width: "100%",
          border: "none",
          outline: "none",
          padding: multilinePadding,
          minHeight
        }
      : {
          boxSizing: "border-box",
          height: "100%",
          width: "100%",
          padding: "0 -14px"
        }
  }
}

const getMentionStyles = (theme, { fontSize, lineHeight }) => ({
  fontSize: fontSize || 16,
  lineHeight: lineHeight || "20px",
  position: "relative",
  marginTop: 1,
  zIndex: 1,
  color: theme.palette.text.link,
  textShadow: "1px 1px 1px white, 1px -1px 1px white, -1px 1px 1px white -1px -1px 1px white",
  backgroundColor: theme.palette.background.default,
  pointerEvents: "none"
})

const markup = "@[__display__](__id__)"
const mentionRegex = markupToRegex(markup)
const triggerRegex = /(@([\w]+))$/

const getMentionChildren = (data, style, onAdd, toggleSubMenu) => (
  <Mention
    key="root"
    trigger={triggerRegex}
    data={data}
    style={style}
    markup={markup}
    regex={mentionRegex}
    appendSpaceOnAdd
    onAdd={onAdd}
    renderSuggestion={(
      { display, hidden, showSubMenu, subMenuFocusIndex, source },
      search,
      highlightedDisplay,
      index,
      focused,
      addManually,
      stateMutator
    ) => {
      const { __typename: type } = source
      if (hidden && !showSubMenu) return null

      switch (type) {
        case "User":
          return <UserMentionListItem item={source} />
        case "Group": {
          const groupMentionProps = {
            text: source.name,
            secondaryText: "Group of users",
            iconName: "group",
            avatarId: source.id
          }
          return <MentionListItem {...groupMentionProps} />
        }
        case "Knowledge":
        case "KnowledgeV2":
        case "KnowledgeArticle":
          return <MentionListItem text={source.title} secondaryText="Knowledge base article" iconName="knowledge" />
        // case "Category":
        //   return <MentionListItem text={source.name} secondaryText="Knowledge base category" iconName="custom" customIconName={source.icon} />
        case "MentionableProcess":
          return (
            <MentionProcessListItem
              text={source.name}
              data={source}
              secondaryText="Process"
              {...{
                showSubMenu,
                toggleSubMenu,
                addManually,
                stateMutator,
                subMenuFocusIndex
              }}
            />
          )
        case "MentionableTask":
          return <MentionListItem text={`${source.name}`} secondaryText={`Task - ${source.location}`} iconName="task" />

        default:
          return display
      }
    }}
  />
)

const convertProcessJobToMention = ({ id, name, locationDay, location, __typename }) => ({
  id: `${__typename}:${id}:${locationDay}:${location.id}:${location.name}`,
  display: `${name}`
})

const KEY = {
  TAB: 9,
  RETURN: 13,
  ESC: 27,
  UP: 38,
  DOWN: 40,
  RIGHT: 39,
  LEFT: 37
}

const MentionTextInput = ({
  value,
  onChange,
  onAddMention,
  label,
  placeholder = null,
  height,
  minHeight = null,
  fontSize,
  lineHeight,
  inputRef,
  multiline = false,
  variant = "outlined",
  margin,
  locations,
  users: passedUsers,
  groups: passedGroups,
  cy,
  skipUsers = false,
  skipGroups = false,
  skipKnowledges = false,
  skipJobs = false,
  portalHost = null,
  ...rest
}) => {
  const { location } = useAuth()
  const { data: usersData } = useQueryUsers({ skip: skipUsers || !!passedUsers })
  const { data: groupsData } = useQueryGroups({ skip: skipGroups || !!passedGroups })
  const { data: articles } = useQueryKnowledges({
    variables: { limit: undefined, isArticle: true },
    skip: skipKnowledges
  })
  const { data: jobs } = useQueryMentionableJobs({
    variables: {
      locations: mapToIds(locations?.length > 0 ? locations : location ? [location] : [])
    },
    skip: skipJobs
  })
  const [data, setData] = useState([])

  const theme = useTheme()
  const [showingSubMenu, setShowingSubMenu] = useState(false)

  const style = getStyles(theme, {
    height,
    minHeight,
    multiline,
    showingSubMenu,
    margin
  })
  const mentionStyle = getMentionStyles(theme, { fontSize, lineHeight })

  useEffect(() => {
    const newData = []
    if (jobs) {
      newData.push(
        ...jobs.mentionableJobs.processes.map((d) => ({
          display: `${d.name}`,
          id: `${d.__typename}:${d.id}`,
          hidden: false,
          hasSubMenu: true,
          showSubMenu: false,
          source: { name: d.name, jobs: d.jobs, __typename: d.__typename }
        }))
      )
      newData.push(
        ...jobs.mentionableJobs.tasks.map((d) => ({
          display: `${d.name}`,
          id: `${d.__typename}:${d.id}:${d.locationDay}`,
          source: {
            name: d.name,
            location: d.location?.name,
            __typename: d.__typename
          }
        }))
      )
    }

    if (articles)
      newData.push(
        ...articles.knowledges
          .filter((d) => d.__typename === "KnowledgeArticle")
          .map((d) => ({
            display: `${d.title}`,
            id: `${d.__typename}:${d.id}`,
            source: { title: d.title, __typename: d.__typename }
          }))
      )

    const users = passedUsers || usersData?.users
    if (users)
      newData.push(
        ...users.map((d) => ({
          display: `${d.fullName}`,
          id: `${d.__typename}:${d.id}`,
          source: {
            id: d.id,
            fullName: d.fullName,
            firstName: d.firstName,
            lastName: d.lastName,
            avatar: d.avatar,
            __typename: d.__typename
          }
        }))
      )

    const groups = passedGroups || groupsData?.groups
    if (groups)
      newData.push(
        ...groups.map((d) => ({
          display: `${d.name}`,
          id: `${d.__typename}:${d.id}`,
          source: { id: d.id, name: d.name, __typename: d.__typename }
        }))
      )

    setData(newData)
  }, [articles, groupsData, jobs, usersData, passedUsers, passedGroups])

  const inputLabelProps = value ? { shrink: true } : {}

  const handleAddMention = (id, display) => {
    if (onAddMention) onAddMention(id, display)
  }

  const handleToggleSubMenu = ({ mutateState }) => {
    mutateState(updateSubMenuShowState)
  }

  const getSuggestionStateInfo = (state) => {
    const suggestions = state.suggestions[0]
    const { results } = suggestions
    const suggestion = results[state.focusIndex]
    return [results, suggestion, suggestions.queryInfo]
  }

  const updateSubMenuShowState = (state) => {
    const [results, suggestion] = getSuggestionStateInfo(state)
    if (suggestion.hasSubMenu) {
      const showing = suggestion.showSubMenu
      results.forEach((result) => {
        if (result.id === suggestion.id) return
        result.hidden = !showing
      })
      suggestion.subMenuFocusIndex = 0
      suggestion.showSubMenu = !showing
      setShowingSubMenu(!showing)
    }
    return { ...state }
  }

  const updateSubMenuFocus = (state, delta) => {
    const [, suggestion] = getSuggestionStateInfo(state)
    if (suggestion.hasSubMenu) {
      const jobsCount = suggestion.source.jobs.length
      suggestion.subMenuFocusIndex = (jobsCount + suggestion.subMenuFocusIndex + delta) % jobsCount
    }
    return { ...state }
  }

  const addSelectedSubMenuItem = (state, addMention) => {
    const [, suggestion, queryInfo] = getSuggestionStateInfo(state)
    const job = suggestion.source.jobs[suggestion.subMenuFocusIndex]
    addMention(convertProcessJobToMention(job), queryInfo)
  }

  const handleKeyDownOverride = (
    ev,
    { clearSuggestions, shiftFocus, selectFocused, mutateState, state, addMention }
  ) => {
    switch (ev.keyCode) {
      case KEY.DOWN: {
        if (!showingSubMenu) shiftFocus(+1)
        else mutateState((prev) => updateSubMenuFocus(prev, +1))
        return
      }
      case KEY.UP: {
        if (!showingSubMenu) shiftFocus(-1)
        else mutateState((prev) => updateSubMenuFocus(prev, -1))
        return
      }
      case KEY.RIGHT: {
        mutateState(updateSubMenuShowState)
        return
      }
      case KEY.LEFT: {
        if (showingSubMenu) mutateState(updateSubMenuShowState)
        else clearSuggestions()
        return
      }
      case KEY.RETURN: {
        if (showingSubMenu) addSelectedSubMenuItem(state, addMention)
        else if (state.suggestions[0].results[state.focusIndex].hasSubMenu) mutateState(updateSubMenuShowState)
        else selectFocused()
        return
      }
      case KEY.TAB: {
        selectFocused()
        break
      }
      default:
    }
  }

  const handleClearSuggestions = () => {
    setShowingSubMenu(false)
  }

  return (
    <TextField
      {...rest}
      multiline={multiline}
      variant={variant}
      margin={margin}
      label={label}
      placeholder={label ? undefined : placeholder}
      InputProps={{
        style: { padding: 0 },
        inputComponent: MentionsInput,
        inputProps: {
          value: value || "",
          singleLine: !multiline,
          onChange,
          inputRef,
          onKeyDownOverride: handleKeyDownOverride,
          onClearSuggestions: handleClearSuggestions,
          onBlur: handleClearSuggestions,
          // mentionHintTrigger: "@",
          // mentionHint: "Mention a teammate, job or knowledge base article",
          keyDownOverrideTriggerCodes: KEY,
          children: getMentionChildren(data, mentionStyle, handleAddMention, handleToggleSubMenu),
          markup,
          style,
          "data-cy": cy,
          placeholder,
          suggestionsPortalHost: portalHost
        }
      }}
      InputLabelProps={inputLabelProps}
    />
  )
}

export { MentionTextInput, getMentionChildren, convertProcessJobToMention }
